import { useState, useMemo, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useUploadPetProfileImageMutation } from '../../data/pet';
import { BrandContext } from '../../contextProviders/brandContext';
import { FeatureFlagContext } from '../../contextProviders/featureFlagContext';
import { getCroppedImg } from '../../helpers/cropImageHelper';
import { SLICES } from '../../constants/slices';

export const usePetImageUploadPopup = (
  FullPolicyNumber,
  species,
  gender,
  profileImage,
  setPetProfileImage,
  coverImage,
  setCoverImage,
  handleClose
) => {
  const [uploadPetProfileImage] = useUploadPetProfileImageMutation();
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isV3PenTestRemediationEndpointsEnabled = cspConfiguration?.IsV3PenTestRemediationEndpointsEnabled;
  const { auth0AccessToken } = useSelector((state) => state?.[SLICES.AUTHENTICATION]?.authDetails || {});
  const [croppedPetImageAreaPixels, setCroppedPetImageAreaPixels] = useState(null);
  const [newProfileImage, setNewProfileImage] = useState(profileImage);
  const [profileFileName, setProfileFileName] = useState(null);
  const [croppedPetImage, setCroppedPetImage] = useState(null);

  const [newCoverImage, setNewCoverImage] = useState(coverImage);
  const [croppedCoverImageAreaPixels, setCroppedCoverImageAreaPixels] = useState(null);
  const [coverFileName, setCoverFileName] = useState(null);
  const [croppedCoverImage, setCroppedCoverImage] = useState(null);

  const [updateIsLoading, setUpdateIsLoading] = useState(false);

  const {
    activeBrandContext: {
      brand: { partnerPrefix }
    }
  } = useContext(BrandContext);

  const iconCssClass = useMemo(() => {
    if (species === 'Canine') {
      return `dog-profile-${gender}`.toLowerCase();
    }
    return `cat-profile-${gender}`.toLowerCase();
  }, [species, gender]);

  useEffect(() => {
    if (croppedPetImage) {
      setPetProfileImage(croppedPetImage);
    }
    if (croppedCoverImage) {
      setCoverImage(croppedCoverImage);
    }
  }, [croppedPetImage, croppedCoverImage, setCoverImage, setPetProfileImage]);

  const createFormData = (file, type, fileName) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('fullPolicyNumber', FullPolicyNumber);
    formData.append('brandPartner', partnerPrefix);
    formData.append('fileName', fileName);
    return formData;
  };

  const handleImageUpdates = async () => {
    if (isV3PenTestRemediationEndpointsEnabled && !auth0AccessToken) return;

    setUpdateIsLoading(true);
    // Upload Pet Profile Image
    if (newProfileImage && croppedPetImageAreaPixels) {
      const petProfileBlob = await getCroppedImg(newProfileImage, croppedPetImageAreaPixels);
      const petProfileImageUrl = URL.createObjectURL(petProfileBlob);
      setCroppedPetImage(petProfileImageUrl);
      const petProfilefile = new File([petProfileBlob], profileFileName, { type: petProfileBlob.type });
      await uploadPetProfileImage({
        data: createFormData(petProfilefile, 'profile', profileFileName),
        auth0Token: auth0AccessToken,
        isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
      });
    }

    // Upload Pet Cover Image
    if (newCoverImage && croppedCoverImageAreaPixels) {
      const petCoverBlob = await getCroppedImg(newCoverImage, croppedCoverImageAreaPixels);
      const petCoverImageUrl = URL.createObjectURL(petCoverBlob);
      setCroppedCoverImage(petCoverImageUrl);
      const petCoverfile = new File([petCoverBlob], coverFileName, { type: petCoverBlob.type });
      await uploadPetProfileImage({
        data: createFormData(petCoverfile, 'cover', coverFileName),
        auth0Token: auth0AccessToken,
        isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
      });
    }
    setUpdateIsLoading(false);
    handleClose();
  };

  return {
    handleImageUpdates,
    updateIsLoading,
    iconCssClass,
    newProfileImage,
    setNewProfileImage,
    setCroppedPetImageAreaPixels,
    setProfileFileName,
    newCoverImage,
    setNewCoverImage,
    setCroppedCoverImageAreaPixels,
    setCoverFileName
  };
};
